import React, { useEffect, useRef, useState } from 'react';
import ImagePicker from '../ImagePicker';
import { copyDirectoryPhoto, errorText, listDirectory } from './apiDirectory';
import { DAY_LABELS, mediaUrl, moveItem, attributesToRows } from './directoryHelpers';

/* Form controls shared by the directory editors. */

/** Toggle chips over a fixed option list; value is the ordered list of keys. */
export function ChipSelect({ id, options, value, onChange, emptyText = 'No options defined.' }) {
  const selected = value || [];
  const known = new Set(options.map((o) => o.key));
  const unknown = selected.filter((k) => !known.has(k));
  if (options.length === 0 && unknown.length === 0) return <p className="muted small" style={{ margin: 0 }}>{emptyText}</p>;
  const toggle = (key) => onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  return (
    <div className="chip-group" id={id} role="group">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`chip${selected.includes(o.key) ? ' active' : ''}`}
          aria-pressed={selected.includes(o.key)}
          onClick={() => toggle(o.key)}
        >
          {o.label}
        </button>
      ))}
      {unknown.map((k) => (
        <button key={k} type="button" className="chip active" title="Not in this domain's vocab; click to remove" onClick={() => toggle(k)}>
          {k} ⚠
        </button>
      ))}
    </div>
  );
}

/** Per-day open/close editor. `days` comes from hoursToDays(). */
export function HoursEditor({ days, onChange, errors = {} }) {
  const setDay = (i, patch) => onChange(days.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const setInterval = (i, k, patch) => setDay(i, {
    intervals: days[i].intervals.map((iv, j) => (j === k ? { ...iv, ...patch } : iv)),
  });
  return (
    <div className="stack" style={{ gap: 6 }}>
      {days.map((d, i) => (
        <div key={d.day} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <strong style={{ width: 40 }}>{DAY_LABELS[d.day]}</strong>
          <label className="field-inline" style={{ minWidth: 80 }}>
            <input type="checkbox" checked={d.closed} onChange={(e) => setDay(i, { closed: e.target.checked })} />
            Closed
          </label>
          {!d.closed && d.intervals.map((iv, k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="time" className="input" style={{ width: 120 }} aria-label={`${DAY_LABELS[d.day]} opens`}
                     value={iv.open} onChange={(e) => setInterval(i, k, { open: e.target.value })} />
              –
              <input type="time" className="input" style={{ width: 120 }} aria-label={`${DAY_LABELS[d.day]} closes`}
                     value={iv.close} onChange={(e) => setInterval(i, k, { close: e.target.value })} />
              {d.intervals.length > 1 && (
                <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove interval"
                        onClick={() => setDay(i, { intervals: d.intervals.filter((_, j) => j !== k) })}>×</button>
              )}
            </span>
          ))}
          {!d.closed && (
            <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => setDay(i, { intervals: [...d.intervals, { open: '13:00', close: '17:00' }] })}>
              + split
            </button>
          )}
          {errors[d.day] && <span className="error" role="alert">{errors[d.day]}</span>}
        </div>
      ))}
    </div>
  );
}

/** Key/value table for listing attributes; rows are {key, value} strings. */
export function AttributesEditor({ rows, onChange }) {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="stack" style={{ gap: 6 }}>
      {rows.length === 0 && <p className="muted small" style={{ margin: 0 }}>No attributes.</p>}
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input className="input mono" style={{ flex: '1 1 140px' }} placeholder="key, e.g. water_temp_f" aria-label="Attribute key"
                 value={r.key} onChange={(e) => set(i, { key: e.target.value })} />
          <input className="input" style={{ flex: '2 1 180px' }} placeholder="value (true/false and numbers are typed)" aria-label="Attribute value"
                 value={r.value} onChange={(e) => set(i, { value: e.target.value })} />
          <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove attribute"
                  onClick={() => onChange(rows.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange([...rows, { key: '', value: '' }])}>
          + Add attribute
        </button>
      </div>
    </div>
  );
}
AttributesEditor.fromAttributes = attributesToRows;

const thumbStyle = { width: 96, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)', background: '#f3f4f6' };

/**
 * Directory photos: picks from the media library, has the backend copy the
 * item into the website bucket, and stores the returned media name.
 * `multiple` edits a list (photos, portfolio); otherwise a single name.
 */
export function MediaField({ domainId, slug, value, onChange, multiple = false, onError }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const names = multiple ? (value || []) : (value ? [value] : []);

  const onPicked = async (image) => {
    setPicking(false);
    if (!slug) {
      onError?.('Set the slug before adding photos.');
      return;
    }
    setBusy(true);
    try {
      const { name } = await copyDirectoryPhoto(domainId, image._id, slug);
      if (multiple) onChange([...names.filter((n) => n !== name), name]);
      else onChange(name);
    } catch (err) {
      onError?.(errorText(err, 'Failed to copy the image'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {names.map((n, i) => (
          <figure key={n} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <img src={mediaUrl(n)} alt="" style={thumbStyle} onError={(e) => { e.target.style.opacity = 0.3; }} />
            <figcaption className="muted small mono" style={{ maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n}>{n}</figcaption>
            <span style={{ display: 'flex', gap: 2 }}>
              {multiple && i > 0 && (
                <button type="button" className="btn btn-ghost btn-sm" aria-label="Move earlier"
                        onClick={() => onChange(moveItem(names, i, i - 1))}>←</button>
              )}
              <button type="button" className="btn btn-ghost btn-danger-text btn-sm"
                      onClick={() => (multiple ? onChange(names.filter((x) => x !== n)) : onChange(null))}>Remove</button>
            </span>
          </figure>
        ))}
      </div>
      {(multiple || names.length === 0) && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPicking(true)} disabled={busy}>
          {busy ? 'Copying…' : multiple ? '+ Add photo' : 'Choose photo'}
        </button>
      )}
      {!multiple && names.length > 0 && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPicking(true)} disabled={busy}>
          {busy ? 'Copying…' : 'Replace'}
        </button>
      )}
      {picking && <ImagePicker onSelect={onPicked} onClose={() => setPicking(false)} />}
    </div>
  );
}

/**
 * Search-select over the domain's listings; value is an ordered slug list
 * (first is primary). Names for already-chosen slugs are shown when known.
 */
export function ListingPicker({ domainId, value, onChange, noun = 'listing' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [names, setNames] = useState({});
  const timer = useRef(null);
  const selected = value || [];

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      try {
        const data = await listDirectory(domainId, 'listings', { q: q.trim(), limit: 10 });
        setResults(data.items || []);
        setNames((prev) => ({ ...prev, ...Object.fromEntries((data.items || []).map((it) => [it.slug, `${it.name}${it.city ? `, ${it.city}` : ''}`])) }));
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, domainId]);

  return (
    <div>
      {selected.length > 0 && (
        <ol style={{ margin: '0 0 8px', paddingLeft: 20 }}>
          {selected.map((s, i) => (
            <li key={s} style={{ marginBottom: 4 }}>
              <span>{names[s] || <span className="mono">{s}</span>}</span>
              {i === 0 && <span className="muted small"> · primary</span>}
              {i > 0 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(moveItem(selected, i, 0))}>Make primary</button>
              )}
              <button type="button" className="btn btn-ghost btn-danger-text btn-sm" onClick={() => onChange(selected.filter((x) => x !== s))}>Remove</button>
            </li>
          ))}
        </ol>
      )}
      <input type="search" className="input" placeholder={`Search ${noun} by name…`} aria-label={`Search ${noun}`}
             value={q} onChange={(e) => setQ(e.target.value)} />
      {results.length > 0 && (
        <div className="stack" style={{ gap: 2, marginTop: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, padding: 4 }}>
          {results.map((r) => (
            <button key={r.slug} type="button" className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    disabled={selected.includes(r.slug)}
                    onClick={() => { onChange([...selected, r.slug]); setQ(''); }}>
              {r.name}{r.city ? <span className="muted">&nbsp;· {r.city}</span> : null}
              {selected.includes(r.slug) && <span className="muted">&nbsp;(added)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
